import React, { useMemo } from 'react';
import classNames from 'classnames';
import { EnterpriseDashboardTestIds } from '@trello/webdriverio-tests';
import { forNamespace } from 'app/common/lib/util/i18n';
import Icon, { IconName, IconColor } from 'app/common/components/controls/icon';
import {
  PopoverMenu,
  PopoverMenuButton,
} from 'app/gamma/src/components/popover/popover-menu';

import styles from './PageHeaderSort.less';
import { usePopover, Popover } from '@trello/nachos';

const formatTitle = forNamespace('view title');

class InvalidSelectedSortOption extends Error {
  constructor(selectedSortOptionName: string) {
    const message = `Selected sort option ${selectedSortOptionName} was not found in the provided sortOptions`;
    super(message);
    this.name = 'InvalidSelectedSortOption';
  }
}

interface SortOption {
  name: string;
  // The text that shows as part of the button
  // when this sort option is selected
  selectedDisplay: string;
  // The text that shows in the popover menu
  // for this option
  menuDisplay: string;
}

interface PageHeaderSortProps {
  selectedSortOptionName: SortOption['name'];
  onClickSortOption: (sortOptionName: SortOption['name']) => void;
  sortOptions: SortOption[];
}

export const PageHeaderSort = (props: PageHeaderSortProps) => {
  const { selectedSortOptionName, onClickSortOption, sortOptions } = props;
  const { triggerRef, popoverProps, toggle } = usePopover<HTMLSpanElement>();

  const selectedSortOptionDisplay = useMemo(() => {
    const selectedSortOption = sortOptions.find((so) => {
      return so.name === selectedSortOptionName;
    });
    if (!selectedSortOption) {
      throw new InvalidSelectedSortOption(selectedSortOptionName);
    }
    return selectedSortOption.selectedDisplay;
  }, [selectedSortOptionName, sortOptions]);

  return (
    <>
      <span role="button" ref={triggerRef} onClick={toggle}>
        <span className={styles.sorting}>{selectedSortOptionDisplay}</span>
        <span className={styles.sortButtonIcon}>
          <Icon name={IconName.OverflowMenuHorizontal} small />
        </span>
      </span>
      <Popover {...popoverProps} title={formatTitle('sort by')}>
        <PopoverMenu className={styles.negatePopoverPadding}>
          {sortOptions.map((so) => (
            <div
              data-test-id={`${EnterpriseDashboardTestIds.SortOptionButton}${so.name}`}
              key={so.name}
            >
              <PopoverMenuButton onClick={() => onClickSortOption(so.name)}>
                <div className={styles.sortPopoverItem}>
                  <span className={styles.name}>{so.menuDisplay}</span>
                  {selectedSortOptionName === so.name && (
                    <span
                      className={classNames(styles.selectedMarker)}
                      data-test-id={
                        EnterpriseDashboardTestIds.SortOptionSelectedMarker
                      }
                    >
                      <Icon
                        name={IconName.Check}
                        color={IconColor.Light}
                        small
                      />
                    </span>
                  )}
                </div>
              </PopoverMenuButton>
            </div>
          ))}
        </PopoverMenu>
      </Popover>
    </>
  );
};
